<template>
    <CRow>
        <CCol col="12">
            <CCard>
                <CCardHeader class="d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">
                        Scholarship Details
                        <CBadge v-if="scholarshipDetails" :color="getVerifiedStatusColor(scholarshipDetails.isVerified)" class="ml-2">
                            {{ getVerifiedStatusLabel(scholarshipDetails.isVerified) }}
                        </CBadge>
                    </h5>
                    <CButton color="primary" @click="goBack">Back to List</CButton>
                </CCardHeader>
                <CCardBody v-if="scholarshipDetails">
                    <CAlert v-if="scholarshipDetails.isVerified === 4" color="danger" class="mb-4 shadow-sm">
                        <h6 class="alert-heading font-weight-bold"><i class="fas fa-exclamation-triangle mr-2"></i>Rejection Reason</h6>
                        <hr>
                        <p class="mb-0" style="white-space: pre-wrap;">{{ scholarshipDetails.rejectedReason || 'No reason provided.' }}</p>
                    </CAlert>

                    <CCard class="mt-2 shadow-sm">
                        <CCardHeader class="bg-info text-white">
                            <h6 class="mb-0">General Information</h6>
                        </CCardHeader>
                        <CCardBody>
                            <CRow>
                                <CCol md="6">
                                    <p><strong>Title:</strong> {{ scholarshipDetails.title }}</p>
                                    <p><strong>Slug:</strong> {{ scholarshipDetails.slug }}</p>
                                    <p><strong>Type:</strong> {{ scholarshipDetails.scholarshipType ? scholarshipDetails.scholarshipType.title : 'N/A' }}</p>
                                    <p><strong>Verification Status:</strong> 
                                        <CBadge :color="getVerifiedStatusColor(scholarshipDetails.isVerified)">
                                            {{ getVerifiedStatusLabel(scholarshipDetails.isVerified) }}
                                        </CBadge>
                                    </p>
                                    <p>
                                    <strong>Provider Type:</strong>
                                    {{
                                        scholarshipDetails.providerType && scholarshipDetails.providerType.length
                                        ? scholarshipDetails.providerType.map(t => t.title).join(', ')
                                        : 'N/A'
                                    }}
                                    </p>
                                    <p><strong>Academic Year:</strong> {{ scholarshipDetails.academicYear }}</p>
                                    <p><strong>Application Mode:</strong> {{ scholarshipDetails.applicationMode ? scholarshipDetails.applicationMode.title : 'N/A' }}</p>
                                    <!-- Application URL (if Online) -->
                                    <p v-if="scholarshipDetails.applicationMode && scholarshipDetails.applicationMode.title === 'Online'">
                                        <strong>Application URL:</strong>
                                        <a
                                            :href="formatUrl(scholarshipDetails.applicationUrl)"
                                            target="_blank"
                                        >
                                            {{ scholarshipDetails.applicationUrl || 'N/A' }}
                                        </a>
                                        </p>

                                        <!-- Application Address (if Offline) -->
                                        <div v-else>
                                        <p><strong>Application Address:</strong></p>

                                        <ul v-if="scholarshipDetails.scholarshipLocation && scholarshipDetails.scholarshipLocation.list && scholarshipDetails.scholarshipLocation.list.length" class="pl-3">
                                            <li
                                            v-for="addr in scholarshipDetails.scholarshipLocation.list"
                                            :key="addr.id"
                                            >
                                            {{ addr.title }}
                                            </li>
                                        </ul>

                                        <p v-else>N/A</p>
                                    </div>
                                </CCol>
                                <CCol md="6">
                                    <p><strong>Summary:</strong> {{ scholarshipDetails.summary }}</p>
                                    <p><strong>Tags:</strong>
                                        <CBadge v-for="tag in scholarshipDetails.tags" :key="tag.id" color="info" class="ml-1">
                                            {{ tag.title }}
                                        </CBadge>
                                        <span v-if="!scholarshipDetails.tags || scholarshipDetails.tags.length === 0">N/A</span>
                                    </p>
                                    <p><strong>Required Documents:</strong>
                                        <CBadge v-for="doc in scholarshipDetails.requiredDocuments" :key="doc.id" color="secondary" class="ml-1">
                                            {{ doc.title }}
                                        </CBadge>
                                        <span v-if="!scholarshipDetails.requiredDocuments || scholarshipDetails.requiredDocuments.length === 0">N/A</span>
                                    </p>
                                    <p><strong>Attachments:</strong>
                                        <div v-if="scholarshipDetails.media && scholarshipDetails.media.length > 0">
                                            <a v-for="(attachment, idx) in scholarshipDetails.media" :key="idx" :href="attachment.url" target="_blank" class="mr-2">
                                                <CBadge color="dark">
                                                    <CIcon name="cil-file" /> {{ attachment.fileName }}
                                                </CBadge>
                                            </a>
                                        </div>
                                        <span v-else>N/A</span>
                                    </p>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>

                    <CCard class="mt-4 shadow-sm">
                        <CCardHeader class="bg-info text-white">
                            <h6 class="mb-0">Academic Requirements</h6>
                        </CCardHeader>
                        <CCardBody>
                            <CRow>
                                <CCol md="6">
                                    <p><strong>Programme:</strong> {{ scholarshipDetails.programme ? scholarshipDetails.programme.title : 'N/A' }}</p>
                                    <p><strong>Stream:</strong> {{ scholarshipDetails.stream ? scholarshipDetails.stream.title : 'N/A' }}</p>
                                    <p><strong>Percentage:</strong> {{ scholarshipDetails.percentage ? scholarshipDetails.percentage + '%' : 'N/A' }}</p>
                                </CCol>
                                <CCol md="6">
                                    <p><strong>Discipline:</strong>
                                        <CBadge v-for="discipline in scholarshipDetails.discipline" :key="discipline.id" color="warning" class="ml-1">
                                            {{ discipline.title }}
                                        </CBadge>
                                        <span v-if="!scholarshipDetails.discipline || scholarshipDetails.discipline.length === 0">N/A</span>
                                    </p>
                                    <p>
                                    <strong>GPA:</strong>
                                    {{
                                        scholarshipDetails.gpa && scholarshipDetails.gpaScale
                                        ? `${parseFloat(scholarshipDetails.gpa)} / ${scholarshipDetails.gpaScale}`
                                        : 'N/A'
                                    }}
                                    </p>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>

                    <CCard class="mt-4 shadow-sm">
                        <CCardHeader class="bg-info text-white">
                            <h6 class="mb-0">Financial & Eligibility Criteria</h6>
                        </CCardHeader>
                        <CCardBody>
                            <CRow>
                                <CCol md="6">
                                    <p><strong>Income:</strong> {{ scholarshipDetails.currency ? scholarshipDetails.currency.currencySymbol : '' }} {{ scholarshipDetails.income || 'N/A' }} ({{ scholarshipDetails.currency ? scholarshipDetails.currency.currencyCode : 'N/A' }})</p>
                                    <p><strong>Min Age:</strong> {{ scholarshipDetails.minAge || 'N/A' }}</p>
                                    <p><strong>Max Age:</strong> {{ scholarshipDetails.maxAge || 'N/A' }}</p>
                                </CCol>
                                <CCol md="6">
                                    <p><strong>Gender:</strong> {{ scholarshipDetails.gender ? scholarshipDetails.gender.title : 'N/A' }}</p>
                                    <p><strong>Languages:</strong>
                                        <CBadge v-for="lang in scholarshipDetails.languages" :key="lang.id" color="primary" class="ml-1">
                                            {{ lang.title }}
                                        </CBadge>
                                        <span v-if="!scholarshipDetails.languages || scholarshipDetails.languages.length === 0">N/A</span>
                                    </p>
                                    <p><strong>Other Requirements:</strong> {{ scholarshipDetails.otherRequirements || 'N/A' }}</p>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>

                    <CCard class="mt-4 shadow-sm">
                        <CCardHeader class="bg-info text-white">
                            <h6 class="mb-0">Application Dates</h6>
                        </CCardHeader>
                        <CCardBody>
                            <CRow>
                                <CCol md="4">
                                    <p><strong>Start Date:</strong> {{ formatDate(scholarshipDetails.applicationStartDate) }}</p>
                                </CCol>
                                <CCol md="4">
                                    <p><strong>End Date:</strong> {{ formatDate(scholarshipDetails.applicationEndDate) }}</p>
                                </CCol>
                                <CCol md="4">
                                    <p><strong>Result Date:</strong> {{ formatDate(scholarshipDetails.resultDate) }}</p>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>

                    <CCard class="mt-4 shadow-sm">
                        <CCardHeader class="bg-info text-white">
                            <h6 class="mb-0">Geographical Coverage</h6>
                        </CCardHeader>

                        <CCardBody>
                            <div v-if="scholarshipDetails.countryData && scholarshipDetails.countryData.length > 0">

                            <div
                                v-for="(country, cIdx) in scholarshipDetails.countryData"
                                :key="cIdx"
                                class="mb-3"
                            >
                                <p><strong>Country:</strong> {{ country.name }}</p>

                                <!-- ✅ States -->
                                <p v-if="country.states && country.states.length > 0">
                                <strong>States:</strong>
                                <CBadge
                                    v-for="state in country.states"
                                    :key="state.id"
                                    color="primary"
                                    class="ml-1"
                                >
                                    {{ state.name }}
                                </CBadge>
                                </p>

                                <p v-else>
                                <strong>States:</strong> N/A
                                </p>

                                <hr v-if="cIdx < scholarshipDetails.countryData.length - 1">
                            </div>

                            </div>

                            <p v-else>N/A</p>
                        </CCardBody>
                    </CCard>

                    <CRow class="mt-4">
                        <CCol md="6">
                            <CCard class="shadow-sm">
                                <CCardHeader class="bg-info text-white">
                                    <h6 class="mb-0">How to Apply</h6>
                                </CCardHeader>
                                <CCardBody>
                                    <p>{{ scholarshipDetails.howToApply || 'N/A' }}</p>
                                </CCardBody>
                            </CCard>
                        </CCol>
                        <CCol md="6">
                            <CCard class="shadow-sm">
                                <CCardHeader class="bg-info text-white">
                                    <h6 class="mb-0">Benefits</h6>
                                </CCardHeader>
                                <CCardBody>
                                    <p>{{ scholarshipDetails.benefits || 'N/A' }}</p>
                                </CCardBody>
                            </CCard>
                        </CCol>
                    </CRow>

                    <CCard class="mt-4 shadow-sm">
                        <CCardHeader class="bg-info text-white">
                            <h6 class="mb-0">Contact / Organization Details</h6>
                        </CCardHeader>
                        <CCardBody>
                            <CRow>
                                <CCol md="6">
                                    <p><strong>Organization Name:</strong> {{ scholarshipDetails.organizationName || 'N/A' }}</p>
                                    <p><strong>University / School Name:</strong> {{ scholarshipDetails.instituteName || 'N/A' }}</p>
                                    <p><strong>Contact Person:</strong> {{ scholarshipDetails.contactPersonName || 'N/A' }}</p>
                                    <p><strong>Designation:</strong> {{ scholarshipDetails.contactPersonTitle || 'N/A' }}</p>
                                    <p><strong>Email:</strong> {{ scholarshipDetails.email || 'N/A' }}</p>
                                </CCol>
                                <CCol md="6">
                                    <p><strong>Phone Number:</strong> {{ formatPhoneNumber(scholarshipDetails.countryCode, scholarshipDetails.mobileNumber) }}</p>
                                    <p><strong>Alternate Phone Number:</strong> {{ formatPhoneNumber(scholarshipDetails.countryCodeAlternate, scholarshipDetails.alternateMobileNumber) }}</p>
                                    <p><strong>Location:</strong> {{ scholarshipDetails.location || 'N/A' }}</p>
                                    <p><strong>Website:</strong> <a :href="scholarshipDetails.website" target="_blank" v-if="scholarshipDetails.website">{{ scholarshipDetails.website }}</a><span v-else>N/A</span></p>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>
                </CCardBody>
                <CCardBody v-else>
                    <CAlert color="info" show>Loading scholarship details...</CAlert>
                </CCardBody>

                <CCardFooter>
                    <CButton color="primary" @click="goBack">Back to List</CButton>
                    </CCardFooter>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
import { scholarship } from '/src/store/url.js'; // Assuming you have your API paths here
import { Mixin } from '/src/mixins/index.js'; // Assuming you have a mixin with axios or data fetching logic
import store from "@/store/store"; // Assuming you use Vuex for global state/loader

export default {
    name: "ScholarshipDetails",
    mixins: [Mixin], // Use your existing Mixin for API calls or utility methods
    data() {
        return {
            scholarshipDetails: null, // This will hold your fetched data
            scholarshipId: this.$route.params.id, // Get ID from URL parameter
        };
    },
    mounted() {
        // Show loader while fetching data
        store.commit('showLoader', true);
        this.fetchScholarshipDetails();
    },
    methods: {
        async fetchScholarshipDetails() {
            try {
                const payload = {
                    "id": this.scholarshipId
                };
                
                axios.post(scholarship.api.view, payload)
                    .then(response => {
                        this.scholarshipDetails = response.data.data;
                    })
                    .catch(error => {
                        console.error('Error:', error);
                        this.scholarshipDetails = null; // Clear data on error
                    });
            } catch (error) {
                console.error("API call failed:", error);
                this.scholarshipDetails = null; // Clear data on error
            } finally {
                // Hide loader after fetching, regardless of success or failure
                store.commit('showLoader', false);
            }
        },
        formatUrl(url) {
            if (!url) return '';
            return url.startsWith('http') ? url : `https://${url}`;
        },
        goBack() {
            // Adjust this route to your scholarship list page
            this.$router.push("/scholarship");
        },
        formatDate(date) {
            if (!date) return 'N/A';
            return new Date(date).toLocaleDateString("en-IN", {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        formatPhoneNumber(countryCode, phoneNumber) {
            if (!phoneNumber) return 'N/A';
            // Ensure countryCode has '+'
            const formattedCountryCode = countryCode && !countryCode.startsWith('+') ? '+' + countryCode : countryCode;
            return `${formattedCountryCode || ''} ${phoneNumber}`;
        },
        getVerifiedStatusLabel(status) {
            const statusMap = {
                1: 'Pending',
                2: 'Verified',
                3: 'Published',
                4: 'Rejected',
                5: 'Blocked',
                6: 'Expired',
                7: 'Draft'
            };
            return statusMap[status] || 'Unknown';
        },
        getVerifiedStatusColor(status) {
            switch (status) {
                case 1: return 'warning';
                case 2: return 'success';
                case 3: return 'primary';
                case 4: return 'danger';
                case 5: return 'dark';
                case 6: return 'secondary';
                case 7: return 'info';
                default: return 'secondary';
            }
        }
    },
};
</script>

<style scoped>
/* Base styling from your example */
p {
    margin-bottom: 0.5rem;
}
h6 {
    margin-bottom: 0;
}
.shadow-sm {
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
}
.ml-1 {
    margin-left: 0.25rem;
}
.mr-2 {
    margin-right: 0.5rem;
}
.mb-3 {
    margin-bottom: 1rem;
}
.badge {
    font-size: 100% !important;
}
</style>